'use client';

import Link from 'next/link';
import { usePathname } from 'next/navigation';

import { serializeJsonLd } from '@/lib/jsonLd';
import { getSiteConfig } from '@/config/sites';
import styles from '@/styles/components/breadcrumb.module.scss';

const site = getSiteConfig();

// Bulgarian labels for known URL segments. null = skip segment (not shown, path still accumulated).
const ROUTE_LABELS = {
  pages: null,
  upload: null,
  mobile: null,
  offline: null,
  covers: 'Корици',
  collections: 'Колекции',
  destinations: 'Дестинации',
  blog: 'Блог',
  'about-us': 'За нас',
  voucher: 'Ваучер',
  checkout: 'Поръчка',
  profile: 'Профил',
  favorites: 'Любими',
  info: 'Информация',
  orders: 'Поръчки',
  'book-preview': 'Преглед на книга',
  privacy: 'Поверителност',
  'terms-conditions': 'Общи условия',
  'register-success': 'Успешна регистрация',
  'reset-password': 'Нова парола',
  'thank-you': 'Благодарим',
  unsubscribe: 'Отписване',
  subscribe: 'Абонамент',
  'verify-email': 'Потвърждение на имейл',
  payment: 'Плащане',
  return: 'Успешно плащане',
  fail: 'Неуспешно плащане',
};

const HIDDEN_PREFIXES = ['/admin', '/pages/admin'];
const HIDDEN_EXACT = new Set(['/', '/project', '/pages/project', '/upload/mobile']);

/**
 * Пътища, които съществуват само като префикс — няма страница зад тях.
 * Трохата се показва като текст, а не като линк: `/destinations` връщаше 404
 * на всичките 755 страници от дървото на дестинациите.
 */
const NON_NAVIGABLE = new Set(['/destinations']);

function formatDynamicSegment(segment) {
  return decodeURIComponent(segment)
    .replace(/-/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function buildCrumbs(pathname) {
  const crumbs = [{ label: 'Начало', path: '/' }];
  const segments = pathname.split('/').filter(Boolean);
  let currentPath = '';

  for (const segment of segments) {
    currentPath += `/${segment}`;

    if (Object.prototype.hasOwnProperty.call(ROUTE_LABELS, segment)) {
      if (ROUTE_LABELS[segment] === null) {
        continue;
      }
      crumbs.push({ label: ROUTE_LABELS[segment], path: currentPath });
    } else {
      crumbs.push({ label: formatDynamicSegment(segment), path: currentPath });
    }
  }

  return crumbs;
}

function buildJsonLd(crumbs) {
  const base = site.domain.metadataBase.replace(/\/$/, '');
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: crumbs.map((crumb, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      name: crumb.label,
      item: `${base}${crumb.path}`,
    })),
  };
}

function breadcrumbJsonLdHtml(jsonLd) {
  return { __html: serializeJsonLd(jsonLd) };
}

export default function Breadcrumb() {
  const pathname = usePathname();

  if (!pathname) {
    return null;
  }
  if (HIDDEN_EXACT.has(pathname)) {
    return null;
  }
  if (HIDDEN_PREFIXES.some((p) => pathname.startsWith(p))) {
    return null;
  }

  const crumbs = buildCrumbs(pathname);
  if (crumbs.length <= 1) {
    return null;
  }

  // На страниците на дестинация/корица САМИТЕ страници емитват по-богат BreadcrumbList
  // (с правилните имена на колекция/корица), затова тук пропускаме JSON-LD, за да няма
  // дублиран/конфликтен BreadcrumbList. Видимата навигация остава.
  const segments = pathname.split('/').filter(Boolean);
  const selfProvidesJsonLd = segments[0] === 'destinations' && segments.length >= 2;

  const jsonLd = buildJsonLd(crumbs);

  return (
    <>
      {selfProvidesJsonLd ? null : (
        <script
          type="application/ld+json"
          suppressHydrationWarning
          dangerouslySetInnerHTML={breadcrumbJsonLdHtml(jsonLd)}
        />
      )}
      <nav aria-label="Хлебни трохи" className={styles.breadcrumb}>
        <div className={styles.inner}>
          <ol className={styles.list}>
            {crumbs.map((crumb, i) => {
              const isLast = i === crumbs.length - 1;
              return (
                <li key={crumb.path} className={styles.item}>
                  {isLast || NON_NAVIGABLE.has(crumb.path) ? (
                    <span
                      {...(isLast ? { 'aria-current': 'page' } : {})}
                      className={isLast ? styles.current : styles.plain}
                    >
                      {crumb.label}
                    </span>
                  ) : (
                    <Link href={crumb.path} className={styles.link}>
                      {crumb.label}
                    </Link>
                  )}
                  {!isLast && (
                    <span aria-hidden="true" className={styles.separator}>
                      /
                    </span>
                  )}
                </li>
              );
            })}
          </ol>
        </div>
      </nav>
    </>
  );
}
